/* =========================================================================
 * Ring Size Modal — behavior
 *
 * Intercepts Add to Cart in the capture phase (before Focal) when the picked
 * size is out of stock but still orderable, and offers the in-stock sizes
 * nearest to it. "I know my size" adds the out-of-stock size unchanged; the X
 * abandons the add entirely. Once the size is settled it hands off to the Care
 * Plan popup so the plan is priced against the final variant.
 *
 * LOAD ORDER: must load BEFORE care-plan.js. Both listen on submit in the
 * capture phase and call stopImmediatePropagation, so first-registered wins.
 * ========================================================================= */
(function () {
  'use strict';

  var POPUP_ID = 'ring-size-modal';
  var pending = null;   // context awaiting a size decision
  var lastCtx = null;
  var lastScreen = 'choice';
  var selfClosing = false;

  /* ---------- helpers ---------- */

  function root() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }
  function addUrl() {
    return (window.themeVariables && themeVariables.routes && themeVariables.routes.cartAddUrl) || (root() + 'cart/add');
  }
  function cartUrl() {
    return (window.themeVariables && themeVariables.routes && themeVariables.routes.cartUrl) || (root() + 'cart');
  }
  function cartIsPage() {
    return window.themeVariables && themeVariables.settings &&
      (themeVariables.settings.cartType === 'page' || themeVariables.settings.pageType === 'cart');
  }

  function parseJSON(el) {
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function getConfig() {
    return parseJSON(document.querySelector('[data-ringsize-config]')) || {};
  }

  // scoped to the nearest .product-form for multi-product pages
  function configElFor(form) {
    var scope = form.closest('.product-form');
    return scope ? scope.querySelector('[data-ringsize-variants]') : null;
  }

  var PAGE_SIZE = 250;
  var MAX_PAGES = 10;
  var variantCache = {};

  function fetchPage(handle, page) {
    return fetch(root() + 'products/' + handle + '.js?page=' + page, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    }).then(function (res) {
      if (!res.ok) throw new Error('product JSON ' + res.status + ' on page ' + page);
      return res.json();
    });
  }

  function fetchAllVariants(handle) {
    var all = [];
    var seen = {};

    function next(page) {
      return fetchPage(handle, page).then(function (json) {
        var batch = (json && json.variants) || [];

        var fresh = batch.filter(function (v) {
          if (seen[v.id]) return false;
          seen[v.id] = true;
          return true;
        });

        all = all.concat(fresh);
        if (fresh.length && batch.length === PAGE_SIZE && page < MAX_PAGES) return next(page + 1);
        return all;
      });
    }

    return next(1);
  }

  function normalize(productJSON, sizePosition) {
    if (!productJSON || !productJSON.variants) return null;
    return {
      sizePosition: sizePosition,
      variants: productJSON.variants.map(function (v) {
        var size = sizePosition === 1 ? v.option1 : sizePosition === 2 ? v.option2 : v.option3;
        return {
          id: v.id,
          size: size,
          options: [v.option1 || null, v.option2 || null, v.option3 || null],
          price: v.price,
          quantity: v.inventory_quantity,
          policy: v.inventory_policy,
          // matches the Liquid `{% if variant.inventory_management %}` test
          tracked: !!v.inventory_management,
          available: v.available
        };
      })
    };
  }

  function loadVariants(configEl) {
    var config = parseJSON(configEl);
    if (!config || !config.handle || !config.sizePosition) return Promise.resolve(null);

    var handle = config.handle;
    if (variantCache[handle]) return variantCache[handle];

    variantCache[handle] = fetchAllVariants(handle).then(function (variants) {
      return normalize({ variants: variants }, config.sizePosition);
    }).catch(function () {
      // Never block Add to Cart on a failed fetch. Cleared so a later attempt
      // can retry rather than being stuck on a cached failure.
      delete variantCache[handle];
      return null;
    });

    return variantCache[handle];
  }

  // Warm the cache on load so the first Add to Cart is instant.
  function prefetch() {
    var els = document.querySelectorAll('[data-ringsize-variants]');
    for (var i = 0; i < els.length; i++) loadVariants(els[i]);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', prefetch);
  } else {
    prefetch();
  }

  function itemFromForm(form) {
    var fd = new FormData(form);
    var item = { id: fd.get('id'), quantity: parseInt(fd.get('quantity') || '1', 10) || 1 };
    var sp = fd.get('selling_plan');
    if (sp) item.selling_plan = sp;
    var props = {};
    var has = false;
    fd.forEach(function (val, key) {
      var m = key.match(/^properties\[(.+)\]$/);
      if (m) { props[m[1]] = val; has = true; }
    });
    if (has) item.properties = props;
    return item;
  }

  /* ---------- sizing logic ---------- */

  // non-numeric sizes disable the feature rather than sorting garbage
  function sizeValue(v) {
    var n = parseFloat(v && v.size);
    return isFinite(n) ? n : null;
  }

  // untracked variants report quantity 0 while being infinitely available
  function isInStock(v) {
    return v.tracked && v.quantity > 0;
  }

  // no stock but still orderable; 'deny' variants go to Klaviyo's BIS form
  function isOversold(v) {
    return v.policy === 'continue' && (!v.tracked || v.quantity <= 0);
  }

  // On a Size x Color product the same size exists once per colour, so an
  // alternative must match every non-size option or we'd silently swap the metal.
  function matchesOtherOptions(v, picked, sizePosition) {
    var a = v.options || [];
    var b = picked.options || [];
    for (var i = 0; i < 3; i++) {
      if (i === sizePosition - 1) continue;
      if ((a[i] || null) !== (b[i] || null)) return false;
    }
    return true;
  }

  function inStockSizesNear(data, picked) {
    var pickedNum = sizeValue(picked);
    if (pickedNum === null) return [];

    return data.variants
      .filter(function (v) {
        return String(v.id) !== String(picked.id) &&
          isInStock(v) &&
          sizeValue(v) !== null &&
          matchesOtherOptions(v, picked, data.sizePosition);
      })
      .sort(function (a, b) {
        var da = Math.abs(sizeValue(a) - pickedNum);
        var db = Math.abs(sizeValue(b) - pickedNum);
        // equidistant sizes sort smaller-first so the order is stable
        return da === db ? sizeValue(a) - sizeValue(b) : da - db;
      });
  }

  var MAX_ALTERNATIVES = 2;

  // One above and one below when both are in stock, else the two nearest. No max gap.
  function pickAlternatives(near, picked) {
    var pickedNum = sizeValue(picked);
    if (pickedNum === null) return [];

    var below = null;
    var above = null;

    for (var i = 0; i < near.length; i++) {
      var n = sizeValue(near[i]);
      if (n < pickedNum) {
        if (!below) below = near[i];
      } else if (n > pickedNum) {
        if (!above) above = near[i];
      }
      if (below && above) break;
    }

    var chosen = below && above ? [below, above] : near.slice(0, MAX_ALTERNATIVES);

    return chosen.sort(function (a, b) { return sizeValue(a) - sizeValue(b); });
  }

  /* ---------- interception ---------- */

  document.addEventListener('submit', onSubmit, true); // capture — before Focal and care-plan

  var replaying = false;

  function setFormBusy(form, busy) {
    if (!form) return;
    var buttons = form.querySelectorAll('[type="submit"]');
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      if (busy) {
        // Remember only buttons we disabled, so a sold-out button stays disabled.
        if (btn.hasAttribute('disabled')) continue;
        btn.setAttribute('data-rs-busy', '');
        btn.setAttribute('disabled', '');
        btn.setAttribute('aria-busy', 'true');
      } else if (btn.hasAttribute('data-rs-busy')) {
        btn.removeAttribute('data-rs-busy');
        btn.removeAttribute('disabled');
        btn.removeAttribute('aria-busy');
      }
    }
  }

  function onSubmit(e) {
    if (replaying) return;

    var form = e.target;
    if (!(form instanceof HTMLFormElement) || form.getAttribute('is') !== 'product-form') return;

    var popup = document.getElementById(POPUP_ID);
    if (!popup) return; // section not added — let the add proceed normally

    var configEl = configElFor(form);
    if (!configEl) return; // not a sized ring — no blob rendered

    var idField = form.querySelector('[name="id"]');
    var variantId = idField && idField.value;
    if (!variantId) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    setFormBusy(form, true);

    var item = itemFromForm(form);

    loadVariants(configEl).then(function (data) {
      if (!data || !data.variants || !data.variants.length) {
        return resume(form);
      }

      var picked = null;
      for (var i = 0; i < data.variants.length; i++) {
        if (String(data.variants[i].id) === String(variantId)) { picked = data.variants[i]; break; }
      }

      if (!picked || !isOversold(picked)) return resume(form);

      // Nothing faster to offer — the modal would only add a step.
      var alternatives = pickAlternatives(inStockSizesNear(data, picked), picked);
      if (!alternatives.length) return resume(form);

      open(popup, { form: form, item: item, picked: picked, alternatives: alternatives });
    }).catch(function () {
      resume(form);
    });
  }

  function resume(form) {
    setFormBusy(form, false);
    replaying = true;
    try {
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    } finally {
      replaying = false;
    }
  }

  /* ---------- popup ---------- */

  function open(popup, ctx) {
    setFormBusy(ctx.form, false);
    pending = ctx;

    var config = getConfig();

    // lowercased because it reads as prose here; the buttons uppercase it
    var intro = popup.querySelector('[data-rs-intro]');
    if (intro) {
      var tpl = intro.getAttribute('data-rs-intro-template') || intro.textContent;
      intro.textContent = tpl.replace('{size}', sizeLabel(ctx.picked, config).toLowerCase());
    }

    // same metafield custom.js reads for the stock message
    var longer = hasLongerLead(ctx.picked.id);
    var etaDefault = popup.querySelector('[data-rs-eta-default]');
    var etaLonger = popup.querySelector('[data-rs-eta-longer]');
    if (etaDefault) etaDefault.hidden = longer;
    if (etaLonger) etaLonger.hidden = !longer;

    renderSizes(popup, ctx.alternatives, config);
    showScreen(popup, 'choice');

    popup.open = true; // Focal modal-content opens
  }

  // reads the metafield blob product-form.liquid outputs for custom.js
  function hasLongerLead(variantId) {
    var el = document.getElementById('hidden-variant-metafields-container');
    if (!el || !el.innerHTML.trim()) return false;
    var rows;
    try { rows = JSON.parse(el.innerHTML); } catch (e) { return false; }
    if (!Array.isArray(rows)) return false;
    for (var i = 0; i < rows.length; i++) {
      if (String(rows[i].variant_id) === String(variantId)) {
        return String(rows[i].metafield_value) === 'true';
      }
    }
    return false;
  }

  function sizeLabel(variant, config) {
    return (config.sizeLabel ? config.sizeLabel + ' ' : '') + variant.size;
  }

  function renderSizes(popup, variants, config) {
    var list = popup.querySelector('[data-rs-size-list]');
    var template = popup.querySelector('[data-rs-size-template]');
    if (!list || !template) return;

    list.textContent = '';

    variants.forEach(function (v) {
      var node = template.content.cloneNode(true);
      var button = node.querySelector('[data-rs-size]');
      var label = node.querySelector('[data-rs-size-label]');
      if (!button) return;
      button.setAttribute('data-rs-size', String(v.id));
      if (label) label.textContent = sizeLabel(v, config);
      list.appendChild(node);
    });
  }

  function showScreen(popup, name, moveFocus) {
    lastScreen = name;
    popup.setAttribute('data-rs-screen', name);
    var panels = popup.querySelectorAll('[data-rs-panel]');
    for (var i = 0; i < panels.length; i++) {
      panels[i].hidden = panels[i].getAttribute('data-rs-panel') !== name;
    }
    var back = popup.querySelector('[data-rs-back]');
    if (back) back.hidden = name !== 'sizes';

    if (!moveFocus) return;
    var target = name === 'sizes'
      ? popup.querySelector('[data-rs-size-list] [data-rs-size]')
      : popup.querySelector('[data-rs-goto-sizes]');
    if (target) target.focus();
  }

  /* ---------- interactions ---------- */

  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    var popup = document.getElementById(POPUP_ID);
    if (!popup) return;

    if (e.target.closest('[data-rs-goto-sizes]')) { e.preventDefault(); showScreen(popup, 'sizes', true); return; }
    if (e.target.closest('[data-rs-back]'))       { e.preventDefault(); showScreen(popup, 'choice', true); return; }

    if (e.target.closest('[data-rs-keep-size]')) {
      e.preventDefault();
      decide(null);
      return;
    }

    var sizeButton = e.target.closest('[data-rs-size]');
    if (sizeButton) {
      e.preventDefault();
      decide(sizeButton.getAttribute('data-rs-size'));
      return;
    }
  });

  // X and overlay both just exit; Focal closes the modal, we drop the context
  (function watchClose() {
    var popup = document.getElementById(POPUP_ID);
    if (!popup) { document.addEventListener('DOMContentLoaded', watchClose); return; }

    var sync = function () {
      var open = popup.hasAttribute('open');
      document.documentElement.classList.toggle('ringsize-open', open);

      if (selfClosing) return;
      if (!open) { pending = null; lastCtx = null; }
    };

    new MutationObserver(sync).observe(popup, { attributes: true, attributeFilter: ['open'] });
    sync();
  })();

  /* ---------- decision ---------- */

  // variantId === null keeps the originally picked (out-of-stock) size.
  function decide(variantId) {
    if (!pending) return;
    var ctx = pending;
    pending = null; // clear first so the close observer doesn't double-fire

    var item = ctx.item;
    var chosen = ctx.picked;
    if (variantId) {
      item.id = variantId;
      for (var i = 0; i < ctx.alternatives.length; i++) {
        if (String(ctx.alternatives[i].id) === String(variantId)) { chosen = ctx.alternatives[i]; break; }
      }
    }

    var popup = document.getElementById(POPUP_ID);
    selfClosing = true;
    if (popup) popup.open = false;
    setTimeout(function () { selfClosing = false; }, 0);

    lastCtx = ctx;

    // variant passed explicitly: the picker still shows the original size, so
    // the Care Plan's own lookup would read a stale price or miss entirely
    if (window.BIYA && window.BIYA.carePlan && typeof window.BIYA.carePlan.offer === 'function') {
      var handled = window.BIYA.carePlan.offer({
        form: ctx.form,
        item: item,
        variant: { id: chosen.id, price: chosen.price, inventory_quantity: chosen.quantity },
        onBack: reopen,
        onDone: function () { lastCtx = null; }
      });
      if (handled) return;
    }

    lastCtx = null;
    addToCart([item]);
  }

  function reopen() {
    if (!lastCtx) return false;

    var popup = document.getElementById(POPUP_ID);
    if (!popup) return false;

    var ctx = lastCtx;
    lastCtx = null;

    var screen = lastScreen === 'sizes' ? 'sizes' : 'choice';

    ctx.item = itemFromForm(ctx.form);

    selfClosing = true;
    try {
      open(popup, ctx);
      showScreen(popup, screen);
    } finally {
      setTimeout(function () { selfClosing = false; }, 0);
    }
    return true;
  }

  window.BIYA = window.BIYA || {};
  window.BIYA.ringSize = window.BIYA.ringSize || {};
  window.BIYA.ringSize.reopen = reopen;

  /* ---------- cart ---------- */

  function addToCart(items) {
    return fetch(addUrl() + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: items, sections: ['mini-cart'] })
    }).then(function (res) {
      return res.json().then(function (data) { return { ok: res.ok, data: data }; });
    }).then(function (r) {
      if (!r.ok) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        return;
      }
      if (cartIsPage()) { window.location.href = root() + 'cart'; return; }
      return fetch(cartUrl() + '.js').then(function (res) { return res.json(); }).then(function (cart) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } }));
        cart.sections = r.data.sections;
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { cart: cart, openMiniCart: true }
        }));
        document.documentElement.dispatchEvent(new CustomEvent('cart-notification:show', {
          bubbles: true,
          detail: { status: 'success', error: '' }
        }));
      });
    }).catch(function () {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    });
  }
})();
